import React, { useEffect, useState } from 'react';import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, GraduationCap, Award, ArrowLeft, ExternalLink, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    api.get(`/programs/${slug}`).then(({ data }) => setProgram(data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-navy/20 border-t-navy animate-spin" /></div>;
  if (!program) return <div className="p-20 text-center text-slate-500">Program not found.</div>;

  const handleEnroll = () => {
    if (program.is_coming_soon) {
      if (!user) { navigate('/login', { state: { from: `/programs/${slug}` } }); return; }
      api.post('/programs/waitlist', { program_slug: slug })
        .then(() => toast.success('You\'re on the waitlist. We\'ll be in touch!'))
        .catch(() => toast.error('Could not join waitlist'));
      return;
    }
    setCheckout(true);
  };

  return (
    <div data-testid="program-detail" className="bg-trjlight">
      <section className="bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Link to="/programs" className="text-white/70 inline-flex items-center text-sm hover:text-gold mb-6" data-testid="back-to-programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> All Programs
            </Link>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">{program.school}</div>
            <h1 className="font-display font-semibold text-3xl lg:text-5xl mt-3 leading-tight">{program.title}</h1>
            <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl">{program.long_description}</p>
            <div className="mt-8 flex gap-6 flex-wrap text-sm text-white/85">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {program.duration_hours} hours</div>
              <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-gold" /> {program.level}</div>
              <div className="flex items-center gap-2"><Award className="h-4 w-4 text-gold" /> Certificate Included</div>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-white text-navy rounded-xl shadow-2xl p-7">
              {program.is_coming_soon ? (
                <div className="text-center">
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold">Coming Soon</div>
                  <div className="font-display font-semibold text-2xl mt-2">Join the Waitlist</div>
                  <p className="text-sm text-slate-600 mt-2">Get early access and launch pricing when this program opens for enrollment.</p>
                  <Button onClick={handleEnroll} className="btn-gold w-full h-12 mt-5" data-testid="waitlist-btn">Notify Me</Button>
                </div>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Tuition</div>
                  <div className="font-serif text-5xl text-navy mt-1">${program.price_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  <div className="text-sm text-slate-500 mt-1">One-time payment · Lifetime access</div>

                  {!checkout ? (
                    <Button onClick={handleEnroll} className="btn-gold w-full h-12 mt-6 text-base" data-testid="enroll-btn">
                      Enroll Now — ${program.price_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Button>
                  ) : (
                    <NcpCheckout slug={slug} program={program} />
                  )}

                  <div className="mt-5 space-y-2 text-sm text-slate-600">
                    {['Lifetime course access', 'Auto-issued certificate on completion', 'Student dashboard & progress tracking', 'Email support from faculty'].map((b) => (
                      <div key={b} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-trjgreen mt-0.5" /> {b}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <h2 className="font-display font-semibold text-2xl text-navy">Learning Outcomes</h2>
          <ul className="mt-6 space-y-3">
            {(program.outcomes || []).map((o) => (
              <li key={o} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-trjgreen mt-0.5 shrink-0" /><span className="text-slate-700">{o}</span></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5">
          <h2 className="font-display font-semibold text-2xl text-navy">Curriculum</h2>
          <div className="mt-6 space-y-3">
            {(program.syllabus || []).map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold">Module {i+1}</div>
                  <div className="text-navy font-semibold mt-0.5">{s.title}</div>
                </div>
                <div className="text-sm text-slate-500">{s.duration}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}


function NcpCheckout({ slug, program }) {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [orderId, setOrderId] = React.useState(null);
  const [claimToken, setClaimToken] = React.useState(null);
  const [opened, setOpened] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // guest fields
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [password, setPassword] = React.useState('');
  // Student Handbook acknowledgment — required before checkout opens.
  const [handbookAck, setHandbookAck] = React.useState(false);

  const startCheckout = async () => {
    if (!handbookAck) {
      toast.error('Please review and acknowledge the Student Handbook before enrolling.');
      return;
    }
    setBusy(true);
    try {
      if (user) {
        const { data } = await api.post('/payments/ncp/start',
          { program_slug: slug, handbook_ack: true });
        setOrderId(data.order_id);
        window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
      } else {
        if (!email || !name) { toast.error('Name and email are required'); setBusy(false); return; }
        const { data } = await api.post('/payments/ncp/guest-start',
          { program_slug: slug, email, name, handbook_ack: true });
        setOrderId(data.order_id);
        setClaimToken(data.claim_token);
        window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
      }
      setOpened(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally { setBusy(false); }
  };

  const claim = async () => {
    setBusy(true);
    try {
      if (user) {
        await api.post('/payments/ncp/claim', { order_id: orderId });
        toast.success('Payment confirmed. Welcome aboard!');
        navigate('/dashboard');
      } else {
        const { data } = await api.post('/payments/ncp/guest-claim',
          { claim_token: claimToken, password: password || null });
        localStorage.setItem('trj_access_token', data.access_token);
        setUser(data.user);
        toast.success('Welcome to TRJ Academy. Account created and program unlocked.');
        if (data.needs_password_setup) {
          toast.info('Check your email for a link to set your password.', { duration: 8000 });
        }
        navigate('/dashboard');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not finalize enrollment.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="paypal-ncp-container">
      {!opened ? (
        <>
          {!user && (
            <div className="space-y-3 bg-trjlight rounded-md p-4 border border-slate-200" data-testid="guest-checkout-fields">
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">Quick enrollment — no account needed</div>
              <div>
                <label className="text-xs font-semibold text-navy">Full name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 focus:border-navy focus:outline-none text-sm"
                  data-testid="guest-name" placeholder="Jane Smith" />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 focus:border-navy focus:outline-none text-sm"
                  data-testid="guest-email" placeholder="you@example.com" />
              </div>
              <div className="text-xs text-slate-500">We'll create your account automatically after payment.</div>
            </div>
          )}
          <div className="bg-trjlight rounded-md p-3 border border-slate-200 flex items-start gap-2.5" data-testid="handbook-ack-row">
            <input
              id="handbook-ack"
              type="checkbox"
              checked={handbookAck}
              onChange={(e) => setHandbookAck(e.target.checked)}
              className="mt-1 h-4 w-4 accent-navy shrink-0"
              data-testid="handbook-ack-checkbox"
            />
            <label htmlFor="handbook-ack" className="text-xs text-slate-700 leading-snug select-none">
              I acknowledge that I have read, understood, and agree to comply with the policies and
              procedures contained in the{' '}
              <Link to="/student-handbook" target="_blank" className="text-navy font-semibold underline underline-offset-2 hover:text-gold">
                TRJ Academy™ Student Handbook
              </Link>.
            </label>
          </div>
          <Button onClick={startCheckout} disabled={busy || !handbookAck} className="btn-gold w-full h-12 text-base" data-testid="paypal-checkout-btn">
            <ExternalLink className="h-4 w-4 mr-2" />
            {busy ? 'Opening PayPal…' : `Pay $${program.price_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })} with PayPal`}
          </Button>
          <div className="text-xs text-slate-500 flex items-start gap-2 leading-relaxed">
            <Shield className="h-3.5 w-3.5 text-trjgreen mt-0.5 shrink-0" />
            Secure PayPal hosted checkout opens in a new tab. After paying, return here and click "I've Completed Payment" to activate enrollment.
          </div>
        </>
      ) : (
        <>
          <div className="bg-trjgreen/5 border border-trjgreen/30 rounded-md p-4 text-sm text-slate-700">
            <div className="font-semibold text-trjgreen mb-1">PayPal checkout opened in a new tab.</div>
            Complete your payment, then return here and click below to finalize your enrollment.
          </div>
          {!user && (
            <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-navy font-semibold">Set your password (optional)</div>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 focus:border-navy focus:outline-none text-sm"
                data-testid="post-payment-password" placeholder="8+ chars, upper, lower, number, special" />
              <div className="text-xs text-slate-500">Leave blank to receive a one-time setup link by email.</div>
            </div>
          )}
          <Button onClick={claim} disabled={busy} className="btn-navy w-full h-12 text-base" data-testid="ncp-claim-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {busy ? 'Activating enrollment…' : "I've Completed Payment — Activate My Enrollment"}
          </Button>
          <button onClick={() => { setOpened(false); setOrderId(null); setClaimToken(null); }}
                  className="block w-full text-xs text-slate-500 hover:text-navy underline">
            Cancel / start over
          </button>
        </>
      )}
    </div>
  );
}
